/**
 * In-lesson repetition.
 *
 * A lesson used to be a straight line: every exercise once, in the authored
 * order, and a wrong answer showed the correction and moved on forever. That
 * is the whole reason a lesson could feel like it taught nothing — a word met
 * once is a word met never.
 *
 * So a lesson is played as a *queue* rather than a list:
 *
 * - The **spine** is the authored order. It is never reordered, because the
 *   content is sequenced deliberately: recognise before hear before say, and
 *   shuffling that would ask her to produce a word she has not met yet.
 * - A **missed exercise comes back** a few steps later, and again after that if
 *   she misses it twice. Nothing she got wrong can end the lesson unresolved.
 * - The lesson closes with a **final round**: a handful of the things she got
 *   right first time, asked once more at a distance. Getting it right once is
 *   recognition; getting it right again after four other questions is the
 *   beginning of memory.
 *
 * The queue is generic over the item type and holds no opinion about what an
 * exercise is — the player decides which items may repeat (speaking and
 * conversation exercises are graded on the server, so they do not) and which
 * count as answered.
 */

/**
 * How far ahead a missed item is re-inserted, counted in spine positions.
 * Four leaves about three other questions in between: enough that the second
 * look is a real recall attempt rather than an echo of the answer still on
 * screen.
 */
export const REPEAT_GAP = 4

/** How many times one item may come back within a single lesson. */
export const MAX_REPEATS = 2

/** Upper bound on the closing round. */
export const FINAL_ROUND_SIZE = 4

/** Why an item is being asked again — the player turns this into a caption. */
export type RepeatReason = 'again' | 'final'

export interface PendingItem<T> {
  item: T
  /** Spine position at which this becomes eligible again. */
  dueAt: number
  /** How many times it has already come back. */
  seen: number
  reason: RepeatReason
}

export interface PracticeState<T> {
  /** The authored order. Read-only once built. */
  spine: readonly T[]
  spineIndex: number
  pending: readonly PendingItem<T>[]
  /** Items answered correctly at the first attempt, in the order she met them. */
  solid: readonly T[]
  finalRoundBuilt: boolean
  /** Items completed so far, for the progress bar. */
  done: number
}

export interface PracticeView<T> {
  item: T
  /** Null on a first encounter. */
  reason: RepeatReason | null
  /** 0 the first time, 1+ on a repeat. Part of the React key, so repeats remount. */
  occurrence: number
  /** True when this came from the authored spine rather than the repeat queue. */
  fromSpine: boolean
}

export function initPractice<T>(spine: readonly T[], startIndex = 0): PracticeState<T> {
  const clamped = Math.min(Math.max(startIndex, 0), spine.length)
  // Run the closing round through the same builder, so an empty lesson — or
  // one resumed past its own end — reports itself finished rather than
  // promising a round that will never be built.
  return buildFinalRound({
    spine,
    spineIndex: clamped,
    pending: [],
    solid: [],
    finalRoundBuilt: false,
    done: clamped,
  })
}

/**
 * The pending item to show next, or -1.
 *
 * While the spine still has items, only repeats that have come due are
 * eligible. Once it is exhausted everything left is eligible, oldest debt
 * first — otherwise a miss scheduled past the end of the lesson would strand
 * the queue forever.
 */
function pickPending<T>(
  state: PracticeState<T>,
): { index: number; entry: PendingItem<T> } | null {
  const spineLeft = state.spineIndex < state.spine.length
  let best: { index: number; entry: PendingItem<T> } | null = null

  for (let i = 0; i < state.pending.length; i++) {
    const candidate = state.pending[i]
    if (!candidate) continue
    if (spineLeft && candidate.dueAt > state.spineIndex) continue
    if (!best || candidate.dueAt < best.entry.dueAt) best = { index: i, entry: candidate }
  }
  return best
}

/** What she should be looking at now, or null when the lesson is over. */
export function currentItem<T>(state: PracticeState<T>): PracticeView<T> | null {
  const pending = pickPending(state)
  if (pending) {
    const { item, reason, seen } = pending.entry
    return { item, reason, occurrence: seen, fromSpine: false }
  }

  const spineItem = state.spine[state.spineIndex]
  if (spineItem === undefined) return null
  return { item: spineItem, reason: null, occurrence: 0, fromSpine: true }
}

/** Consumes the current item and, if the lesson has run dry, opens the final round. */
export function advance<T>(state: PracticeState<T>): PracticeState<T> {
  const pending = pickPending(state)
  const next: PracticeState<T> = pending
    ? {
        ...state,
        pending: state.pending.filter((_, n) => n !== pending.index),
        done: state.done + 1,
      }
    : { ...state, spineIndex: state.spineIndex + 1, done: state.done + 1 }
  return buildFinalRound(next)
}

/**
 * Schedules a missed item to come back.
 *
 * Capped at `MAX_REPEATS`: a word she cannot get right today is a word the
 * spaced-repetition queue should handle tomorrow, not something to trap her in
 * a loop over.
 */
export function requeue<T>(state: PracticeState<T>, view: PracticeView<T>): PracticeState<T> {
  if (view.occurrence >= MAX_REPEATS) return state
  return {
    ...state,
    pending: [
      ...state.pending,
      {
        item: view.item,
        dueAt: state.spineIndex + REPEAT_GAP,
        seen: view.occurrence + 1,
        reason: 'again',
      },
    ],
  }
}

/**
 * Records an item answered correctly at the first attempt, making it a
 * candidate for the closing round. Only first encounters count: a word she got
 * right on the third try has not earned the label.
 */
export function markSolid<T>(state: PracticeState<T>, view: PracticeView<T>): PracticeState<T> {
  if (!view.fromSpine) return state
  return { ...state, solid: [...state.solid, view.item] }
}

function buildFinalRound<T>(state: PracticeState<T>): PracticeState<T> {
  if (state.finalRoundBuilt) return state
  if (state.spineIndex < state.spine.length || state.pending.length > 0) return state

  return {
    ...state,
    finalRoundBuilt: true,
    pending: sampleEvenly(state.solid, FINAL_ROUND_SIZE).map((item) => ({
      item,
      dueAt: state.spineIndex,
      seen: 0,
      reason: 'final' as const,
    })),
  }
}

/**
 * Spreads the picks across the lesson rather than taking the first n, so the
 * closing round revisits the whole lesson instead of only how it opened.
 */
function sampleEvenly<T>(items: readonly T[], n: number): T[] {
  if (items.length <= n) return [...items]
  const stride = items.length / n
  const picked: T[] = []
  for (let i = 0; i < n; i++) {
    const item = items[Math.floor(i * stride)]
    if (item !== undefined) picked.push(item)
  }
  return picked
}

/**
 * How many items are still to come, including the closing round before it
 * exists — counting it from the start keeps the progress bar from sliding
 * backwards when the round appears.
 */
export function remaining<T>(state: PracticeState<T>, projectedFinalRound: number): number {
  const spineLeft = Math.max(0, state.spine.length - state.spineIndex)
  const final = state.finalRoundBuilt ? 0 : Math.min(projectedFinalRound, FINAL_ROUND_SIZE)
  return spineLeft + state.pending.length + final
}
